import React, { createContext, useContext, useState, useEffect } from 'react';

// Crear el contexto
const LanguageContext = createContext();

// Hook personalizado para usar el contexto
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Detectar idioma del navegador
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.languages[0];
  if (browserLang.startsWith('es')) {
    return 'es';
  } else if (browserLang.startsWith('en')) {
    return 'en';
  }
  return 'es'; // Español por defecto
};

// Detectar idioma desde parámetros de URL
const detectLanguageFromURL = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const langParam = urlParams.get('lang');
  if (langParam === 'es' || langParam === 'en') {
    return langParam;
  }
  return null;
};

// Proveedor del contexto
export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Prioridad: URL params > localStorage > browser detection
    const urlLang = detectLanguageFromURL();
    if (urlLang) {
      return urlLang;
    }
    
    // Intentar obtener idioma guardado en localStorage (usar clave estándar i18next)
    const savedLang = localStorage.getItem('i18nextLng') || localStorage.getItem('ybv-language');
    return savedLang || detectBrowserLanguage();
  });

  // Detectar cambios en parámetros de URL
  useEffect(() => {
    const handleURLChange = () => {
      const urlLang = detectLanguageFromURL();
      if (urlLang && urlLang !== language) {
        setLanguage(urlLang);
      }
    };

    // Escuchar cambios en la URL
    window.addEventListener('popstate', handleURLChange);
    return () => window.removeEventListener('popstate', handleURLChange);
  }, [language]);

  // Guardar idioma en localStorage cuando cambie
  useEffect(() => {
    // Guardar en ambas claves para compatibilidad
    localStorage.setItem('i18nextLng', language);
    localStorage.setItem('ybv-language', language);
    // Añadir clase al body para CSS específicos si es necesario
    document.documentElement.lang = language;
  }, [language]);

  const changeLanguage = (newLang) => {
    setLanguage(newLang);
  };

  const value = {
    language,
    changeLanguage,
    isSpanish: language === 'es',
    isEnglish: language === 'en'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};